import type { Form, FormValidator } from "../types";

type FormAction =
  | {
      type: "initialize";
    }
  | {
      type: "change";
      name: string;
      value: string;
    }
  | {
      type: "error";
      name: string;
      error: string | false;
    }
  | {
      type: "blur";
      name: string;
    }
  | {
      type: "setErrors";
      errors: Record<string, string | false>;
    }
  | {
      type: "validate";
    };

const checkIsValid = (errors: Record<string, string | false>) =>
  !Object.values(errors).some((error) => typeof error === "string");

const makeReducer =
  <Keys extends string>({
    validate,
    initialValues,
    initialErrors,
  }: {
    validate: FormValidator<Keys>;
    initialValues?: Record<Keys, string>;
    initialErrors?: Record<Keys, string | false>;
  }) =>
  (state: Form<Keys>["state"], action: FormAction) => {
    switch (action.type) {
      case "initialize": {
        const values = { ...initialValues };
        const errors = initialErrors ?? validate({ ...state, values });
        const isValid = checkIsValid(errors);
        return { ...state, values, errors, isValid };
      }
      case "change": {
        const values = { ...state.values, [action.name]: action.value };
        const touched = { ...state.touched, [action.name]: true };
        const errors = validate({ ...state, touched, values });
        const isValid = checkIsValid(errors);
        return { ...state, values, touched, errors, isValid };
      }
      case "error": {
        const errors = { ...state.errors, [action.name]: action.error };
        const isValid = checkIsValid(errors);
        return { ...state, errors, isValid };
      }
      case "setErrors": {
        const errors = { ...state.errors, ...action.errors };
        const isValid = checkIsValid(errors);
        return { ...state, errors, isValid };
      }
      case "blur": {
        const touched = { ...state.touched, [action.name]: true };
        return { ...state, touched };
      }
      case "validate": {
        const errors = validate({ ...state });
        const isValid = checkIsValid(errors);
        return { ...state, errors, isValid };
      }
      default: {
        console.error("Unknown action: ", action);
        return state;
      }
    }
  };

export default makeReducer;
